import React from 'react';

/**
 * Button styles used across the entire application.
 * Import: import { buttonStyles } from '@/styles/buttons';
 *
 * NOTE: Migrated from adminPageStyles.ts (2025-01-11) and expanded with size variants
 */

// ============================================================================
// PRE-COMPOSED COMBINATIONS (Common use cases - 90% of usage)
// ============================================================================

export const buttonStyles = {
  // Migrated from adminPageStyles - medium size (most common)
  primary: {
    padding: '0.5rem 1.5rem',
    backgroundColor: 'var(--primary-color)',
    color: 'var(--on-primary-color)',
    border: 'none',
    borderRadius: 'var(--radius)',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    fontSize: '1rem',
    minHeight: '36px',
  } as React.CSSProperties,

  secondary: {
    padding: '0.5rem 1.5rem',
    backgroundColor: 'var(--secondary-color)',
    color: 'var(--on-primary-color)',
    border: 'none',
    borderRadius: 'var(--radius)',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    fontSize: '1rem',
    minHeight: '36px',
  } as React.CSSProperties,

  // Migrated from adminPageStyles - small size
  danger: {
    padding: '0.5rem 1rem',
    backgroundColor: 'var(--error-color)',
    color: 'var(--on-primary-color)',
    border: 'none',
    borderRadius: 'var(--radius)',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    fontSize: '0.875rem',
    minHeight: '36px',
  } as React.CSSProperties,

  outline: {
    padding: '0.5rem 1rem',
    backgroundColor: 'transparent',
    color: 'var(--accent-color)',
    border: '1px solid var(--accent-color)',
    borderRadius: 'var(--radius)',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    fontSize: '0.875rem',
    minHeight: '36px',
  } as React.CSSProperties,

  // New variants (expanded from original adminPageStyles)
  primarySmall: {
    padding: '0.30rem 0.8rem',
    backgroundColor: 'var(--primary-color)',
    color: 'var(--on-primary-color)',
    border: 'none',
    borderRadius: 'var(--radius)',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    fontSize: '0.875rem',
    minHeight: '28px',
  } as React.CSSProperties,

  secondarySmall: {
    padding: '0.5rem 1rem',
    backgroundColor: 'var(--secondary-color)',
    color: 'var(--on-primary-color)',
    border: 'none',
    borderRadius: 'var(--radius)',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    fontSize: '0.875rem',
    minHeight: '36px',
  } as React.CSSProperties,

  // Help button (neutral gray for show/hide help toggles)
  help: {
    padding: '0.5rem 1rem',
    backgroundColor: 'var(--text-secondary)',
    color: 'var(--on-primary-color)',
    border: 'none',
    borderRadius: 'var(--radius)',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    fontSize: '0.875rem',
    minHeight: '36px',
  } as React.CSSProperties,

  // Cancel button (neutral, non-committal action)
  cancel: {
    padding: '0.5rem 1rem',
    backgroundColor: 'var(--surface-color)',
    color: 'var(--text-secondary)',
    border: 'none',
    borderRadius: 'var(--radius)',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    fontSize: '0.875rem',
    minHeight: '36px',
  } as React.CSSProperties,
};

// ============================================================================
// BUILDING BLOCKS (For edge cases - 10% of usage)
// ============================================================================

export const buttonBase = {
  border: 'none',
  borderRadius: 'var(--radius)',
  cursor: 'pointer',
  fontWeight: '500',
  transition: 'all 0.2s ease-in-out',
} as React.CSSProperties;

export const buttonSizes = {
  small: {
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    minHeight: '36px',
  } as React.CSSProperties,

  medium: {
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    minHeight: '36px',
  } as React.CSSProperties,

  large: {
    padding: '1rem 2rem',
    fontSize: '1.125rem',
    minHeight: '48px',
  } as React.CSSProperties,
};

export const buttonVariants = {
  primary: {
    backgroundColor: 'var(--primary-color)',
    color: 'var(--on-primary-color)',
  } as React.CSSProperties,

  secondary: {
    backgroundColor: 'var(--secondary-color)',
    color: 'var(--on-primary-color)',
  } as React.CSSProperties,

  danger: {
    backgroundColor: 'var(--error-color)',
    color: 'var(--on-primary-color)',
  } as React.CSSProperties,

  outline: {
    backgroundColor: 'transparent',
    color: 'var(--accent-color)',
    border: '1px solid var(--accent-color)',
  } as React.CSSProperties,

  ghost: {
    backgroundColor: 'transparent',
    color: 'var(--text-primary)',
  } as React.CSSProperties,
};
